import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, useAsync, useSession } from '../ui.jsx';
import { dateLabel, money, monthLabel, unitName } from '../lib/format.js';
import LedgerFilters, { defaultFilters } from './ledger-filters.jsx';
import { Link } from '../main.jsx';

/**
 * 成員分攤主頁 — the default 帳簿控制台 overview. 付款項目 are rows, each 成員 has a
 * column and the group total sits beside them, so three members can reconcile
 * their shares side by side. Every cell shows an HKD amount and a text state.
 */
export default function ConsoleOverview() {
  const session = useSession();
  const [filters, setFilters] = useState(defaultFilters);
  const ledger = useAsync(() => api.ledger(filters), [JSON.stringify(filters)]);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台</div>
          <h1>成員分攤主頁</h1>
          <p className="small muted">支出按付款項目的帳務月份計算；付款按實際確認日期列示。所有金額為 HKD。</p>
        </div>
        <div className="actions">
          <Link to="/console/issue"><button type="button" className="primary">發出付款項目</button></Link>
          <Link to="/console/history"><button type="button">帳簿歷史與匯出</button></Link>
        </div>
      </div>

      <Card title="篩選">
        <LedgerFilters filters={filters} onChange={setFilters} />
      </Card>

      {ledger.loading ? <Loading /> : null}
      {ledger.error ? <ErrorBanner error={ledger.error} /> : null}

      {ledger.data ? (
        <>
          <Card title="期間合計" testId="overview-totals">
            <div className="grid three">
              <div className="metric"><span>支出（不含按金）</span><strong data-testid="total-expenses">{money(ledger.data.overview.expenses_cents)}</strong></div>
              <div className="metric"><span>收入（結餘已退回）</span><strong data-testid="total-income">{money(ledger.data.overview.income_cents)}</strong></div>
              <div className="metric"><span>淨額</span><strong data-testid="total-net">{money(ledger.data.overview.net_cents)}</strong></div>
              <div className="metric"><span>按金權益</span><strong data-testid="total-deposit">{money(ledger.data.overview.deposit_equity_cents)}</strong></div>
              <div className="metric"><span>群組合計</span><strong data-testid="total-group">{money(ledger.data.overview.group_total_cents)}</strong></div>
            </div>
            <p className="small muted" style={{ marginTop: 10 }}>
              支出 = 生效中付款項目總額（不含按金）；收入 = 期間內已退回的結餘；淨額 = 收入 − 支出。按金權益獨立列示，不視為一般消費。
            </p>
          </Card>

          <Card title="成員分攤" subtitle={`${monthLabel(filters.from_month)} 至 ${monthLabel(filters.to_month)}`} testId="allocation-matrix">
            <div className="table-wrap sticky-first">
              <table>
                <thead>
                  <tr>
                    <th>付款項目</th>
                    {ledger.data.column_members.map((member) => (
                      <th key={member.id} className="num">{member.display_name}</th>
                    ))}
                    <th className="num">群組合計</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.data.overview.rows.length === 0 ? (
                    <tr><td colSpan={ledger.data.column_members.length + 2} className="muted small">此期間沒有付款項目。</td></tr>
                  ) : null}
                  {ledger.data.overview.rows.map((row) => (
                    <tr key={row.item_id} data-testid={`overview-row-${row.item_id}`}>
                      <td>
                        <Link to={`/items/${row.item_id}`}>{row.title}</Link>
                        <div className="small muted">
                          {unitName(ledger.data.units, row.unit_id)} · {row.category} · {monthLabel(row.accounting_month)} · 到期 {dateLabel(row.due_date)}
                          {row.status === 'cancelled' ? ' · 已取消' : ''}
                          {row.historical ? ` · 歷史補錄（${row.historical_outcome === 'settled' ? '已結清' : '已取消'}）` : ''}
                          {row.is_deposit ? ' · 按金權益' : ''}
                        </div>
                      </td>
                      {row.cells.map((cell) => (
                        <td className="num" key={cell.member_id} data-testid={`cell-${row.item_id}-${cell.member_id}`}>
                          <div className="cell-amount">
                            <span>{money(cell.amount_cents)}</span>
                            <span className="small muted">{cell.state_label}{cell.overdue ? '（已逾期）' : ''}</span>
                          </div>
                        </td>
                      ))}
                      <td className="num">{money(row.group_total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>成員應付款合計</td>
                    {ledger.data.overview.member_totals.map((total) => (
                      <td className="num" key={total.member_id} data-testid={`member-total-${total.member_id}`}>
                        <div className="cell-amount">
                          <span>{money(total.allocated_cents)}</span>
                          <span className="small muted">仍待付款 {money(total.remaining_cents)}</span>
                        </div>
                      </td>
                    ))}
                    <td className="num">{money(ledger.data.overview.group_total_cents)}</td>
                  </tr>
                  <tr>
                    <td>已確認付款</td>
                    {ledger.data.overview.member_totals.map((total) => (
                      <td className="num" key={total.member_id}>{money(total.confirmed_cents)}</td>
                    ))}
                    <td className="num">
                      {money(ledger.data.overview.member_totals.reduce((sum, total) => sum + total.confirmed_cents, 0))}
                    </td>
                  </tr>
                  <tr>
                    <td>待處理結餘</td>
                    {ledger.data.overview.member_totals.map((total) => (
                      <td className="num" key={total.member_id}>{money(total.pending_balance_cents)}</td>
                    ))}
                    <td className="num">
                      {money(ledger.data.overview.member_totals.reduce((sum, total) => sum + total.pending_balance_cents, 0))}
                    </td>
                  </tr>
                  <tr>
                    <td>按金權益</td>
                    {ledger.data.overview.member_totals.map((total) => (
                      <td className="num" key={total.member_id} data-testid={`member-deposit-${total.member_id}`}>{money(total.deposit_equity_cents)}</td>
                    ))}
                    <td className="num">{money(ledger.data.overview.deposit_equity_cents)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
